import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowsInLight } from 'react-icons/pi';
import Header, { Action } from '../TailwindComponents/Header';
import NotFoundPage from '../TailwindComponents/NotFoundPage';
import CollaboratorStack from '../access/CollaboratorStack';

export interface CollaboratorBadge {
  user_uuid: string;
  name: string;
  initials: string;
  access: string;
  email?: string | null;
  avatar_url?: string | null;
}

interface DetailFrameProps {
  children: ReactNode;
  actions?: Action[];
  dropdownActions?: Action[];
  renderDropdownAsButtons?: boolean;
  subscriptionActive?: boolean;
  breadcrumbs: Array<{ name: string; href: string; current: boolean }>;
  showBackButton?: boolean;
  onBack?: () => void;
  additionalHeaderElements?: ReactNode;
  subHeaderRight?: ReactNode;
  className?: string;
  isFullscreen?: boolean;
  onExitFullscreen?: () => void;
  /**
   * READING MODE ONLY (files-3, files-5).
   *
   * This is the document's name for the slim expanded-view bar - it is NOT
   * rendered in the normal frame, where the breadcrumb already carries the
   * name and repeating it was the "info row" that crowded the viewer.
   */
  fileInfo?: {
    name: string;
    type?: string;
  };
  /**
   * Controls for the reading bar (zoom, Info). Kept deliberately small: in
   * expanded view the point is the document, not the chrome.
   */
  fullscreenActions?: ReactNode;
  collaborators?: CollaboratorBadge[];
  notFound?: boolean;
  /**
   * Anchors for a feature tour that highlights the frame's own chrome.
   *
   * Props rather than constants because the frame is shared: a tour step for
   * "the project header" must not also match a matter's.
   */
  headerTourId?: string;
  dropdownTourId?: string;
  /** Names the overflow menu ("Actions") instead of showing three dots. */
  dropdownLabel?: string;
}

const DetailFrame: React.FC<DetailFrameProps> = ({
  children,
  actions = [],
  dropdownActions = [],
  renderDropdownAsButtons = false,
  subscriptionActive = true,
  breadcrumbs,
  showBackButton = true,
  onBack,
  additionalHeaderElements,
  subHeaderRight,
  className = '',
  isFullscreen = false,
  onExitFullscreen,
  fileInfo,
  fullscreenActions,
  collaborators = [],
  notFound = false,
  headerTourId,
  dropdownTourId,
  dropdownLabel,
}) => {
  const { t } = useTranslation();

  // Esc leaves reading mode (files-5).
  useEffect(() => {
    if (!isFullscreen || !onExitFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExitFullscreen();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen, onExitFullscreen]);

  // Raw rows - the header used to read `c.avatar_url` alone, so a frame handed
  // collaborators in any other shape drew silhouettes.
  const collaboratorBadges = collaborators.length > 0 ? (
    <CollaboratorStack collaborators={collaborators} max={4} size="md" className="ms-2" />
  ) : null;

  // If not found, render the NotFoundPage with back button
  if (notFound) {
    return <NotFoundPage />;
  }

  /*
    READING MODE (files-5).

    Expanded view is the document and almost nothing else: one slim bar with
    the title, and on the right the few controls reading actually needs plus
    collapse. Breadcrumbs, collaborators, the action menu and the file
    navigator all come back on collapse. Esc collapses too.

    There is no Close here, and that is deliberate (files-2): collapsing exits
    a MODE, leaving the document is the breadcrumb's job.
  */
  if (isFullscreen && fileInfo) {
    return (
      <div className="fixed inset-0 z-50 bg-bg overflow-hidden flex flex-col">
        <div className="flex-shrink-0 flex justify-between items-center gap-4 px-4 h-10 glass-bar border-b border-hairline">
          <h1 className="text-sm font-medium text-fg-strong truncate">
            {fileInfo.name}
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            {fullscreenActions}
            <button
              onClick={onExitFullscreen}
              className="flex items-center justify-center p-1.5 rounded transition-colors text-fg-body hover:bg-surface-sunken/70 hover:text-champagne"
              title={t('Collapse')}
              aria-label={t('Collapse')}
            >
              <PiArrowsInLight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Reading surface - everything the bar does not use */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className={`h-full flex flex-col ${className}`}>
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      {/* No fill of its own: list pages render the same Header straight on
          the page background, and a grey band here made DetailFrame pages
          (Contracts, every detail page) sit out of line with them. */}
      <div
        className="flex-shrink-0"
        {...(headerTourId ? { 'data-tour-id': headerTourId } : {})}
      >
        <Header
          // Was accepted and never passed on, so a page's own back target
          // (e.g. branding -> the branding library) was ignored for history.back.
          onBack={onBack}
          dropdownTourId={dropdownTourId}
          actions={actions}
          dropdownActions={dropdownActions}
          renderDropdownAsButtons={renderDropdownAsButtons}
          subscriptionActive={subscriptionActive}
          additionalRightElements={
            <>
              {additionalHeaderElements}
              {collaboratorBadges}
            </>
          }
          breadcrumbs={breadcrumbs}
          dropdownLabel={dropdownLabel}
        />
      </div>
      <div className={`flex-1 overflow-hidden`}>
        <div className={`h-full flex flex-col ${className}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default DetailFrame;
